import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  Button,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Construction,
  Security,
  CalendarMonth,
  Description,
  PhotoCamera,
  Inventory,
  Engineering,
  Assignment,
  HealthAndSafety,
  VerifiedUser,
  ArrowBack,
  Add,
  CloudUpload,
  Edit,
  Delete,
  History,
} from '@mui/icons-material';
import Layout from '../Layout';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTimeBr } from '../utils/datetime';
import { digitsOnly, formatCpf, isValidCpf } from '../utils/masks';

const API_URL = '/api';

const DOCUMENTO_FUNCIONARIO_OPTIONS = [
  { value: 'Ficha de Registro', label: '1. Ficha de Registro' },
  { value: 'Ordem de Serviço', label: '2. Ordem de Serviço' },
  { value: 'ASO', label: '3. ASO' },
  { value: 'Ficha de EPIs', label: '4. Ficha de EPIs' },
];

const DOCUMENTO_FUNCIONARIO_ORDER = new Map(
  DOCUMENTO_FUNCIONARIO_OPTIONS.map((option, index) => [option.value, index])
);

const TREINAMENTO_FUNCIONARIO_OPTIONS = [
  { value: 'NR 06', label: 'NR 06' },
  { value: 'NR 10', label: 'NR 10' },
  { value: 'NR 12', label: 'NR 12' },
  { value: 'NR 35', label: 'NR 35' },
];

const TREINAMENTO_FUNCIONARIO_ORDER = new Map(
  TREINAMENTO_FUNCIONARIO_OPTIONS.map((option, index) => [option.value, index])
);

const TREINAMENTO_VALIDADE_PADRAO_ANOS = {
  'NR 10': 2,
  'NR 35': 2,
};

const VENCIMENTO_ALERTA_DIAS = 30;

const formatDateBr = (value) => {
  if (!value) return '-';

  const normalized = value.toString();
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    const [year, month, day] = normalized.split('-');
    return `${day}/${month}/${year}`;
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return normalized;
  return parsedDate.toLocaleDateString('pt-BR');
};

const addYearsToDate = (value, years) => {
  if (!value || !years) return '';

  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return '';

  const nextDate = new Date(year, month - 1, day);
  nextDate.setFullYear(nextDate.getFullYear() + years);

  const nextYear = nextDate.getFullYear();
  const nextMonth = String(nextDate.getMonth() + 1).padStart(2, '0');
  const nextDay = String(nextDate.getDate()).padStart(2, '0');
  return `${nextYear}-${nextMonth}-${nextDay}`;
};

const getExpirationStatusMeta = (value) => {
  if (!value) {
    return { label: 'Sem vencimento informado', color: 'text.secondary' };
  }

  const reference = new Date(`${value}T00:00:00`);
  if (Number.isNaN(reference.getTime())) {
    return { label: 'Vencimento inválido', color: 'warning.main' };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffInDays = Math.round((reference.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffInDays < 0) {
    return { label: `Vencido há ${Math.abs(diffInDays)} dia(s)`, color: 'error.main' };
  }

  if (diffInDays <= VENCIMENTO_ALERTA_DIAS) {
    return { label: `Vence em ${diffInDays} dia(s)`, color: 'warning.main' };
  }

  return { label: 'Em dia', color: 'success.main' };
};

const renderExpirationStatusCell = (value) => {
  const status = getExpirationStatusMeta(value);

  return (
    <Typography variant="body2" sx={{ fontWeight: 600, color: status.color }}>
      {status.label}
    </Typography>
  );
};

const compareCpfValues = (leftValue, rightValue) => {
  const leftCpf = digitsOnly(leftValue || '');
  const rightCpf = digitsOnly(rightValue || '');

  if (!leftCpf && !rightCpf) return 0;
  if (!leftCpf) return 1;
  if (!rightCpf) return -1;
  return leftCpf.localeCompare(rightCpf, 'pt-BR');
};

const validateDocumentoFuncionarioForm = (formData) => {
  if (!formData.empresa?.trim()) {
    return 'Informe o nome do colaborador para concluir o cadastro do documento.';
  }

  if (!digitsOnly(formData.colaborador_cpf)) {
    return 'Informe o CPF do colaborador para concluir o cadastro do documento.';
  }

  if (!isValidCpf(formData.colaborador_cpf)) {
    return 'Informe um CPF valido para concluir o cadastro do documento.';
  }

  if (formData.tipo_doc === 'ASO' && !formData.data_vencimento) {
    return 'Informe a data de vencimento do ASO para manter o controle atualizado.';
  }

  return '';
};

const validateTreinamentoFuncionarioForm = (formData) => {
  if (!formData.colaborador_nome?.trim()) {
    return 'Informe o nome do colaborador para concluir o cadastro do treinamento.';
  }

  if (!digitsOnly(formData.colaborador_cpf)) {
    return 'Informe o CPF do colaborador para concluir o cadastro do treinamento.';
  }

  if (!isValidCpf(formData.colaborador_cpf)) {
    return 'Informe um CPF valido para concluir o cadastro do treinamento.';
  }

  if (
    formData.data_realizacao &&
    formData.data_vencimento &&
    formData.data_vencimento < formData.data_realizacao
  ) {
    return 'A data de vencimento não pode ser anterior à data de realização.';
  }

  return '';
};

const deriveTrainingFormData = ({ next, fieldName }) => {
  if (fieldName !== 'treinamento_nome' && fieldName !== 'data_realizacao') {
    return next;
  }

  const validityYears = TREINAMENTO_VALIDADE_PADRAO_ANOS[next.treinamento_nome];
  if (!validityYears || !next.data_realizacao) {
    return next;
  }

  return {
    ...next,
    data_vencimento: addYearsToDate(next.data_realizacao, validityYears),
  };
};

const sortDocumentosFuncionarios = (items = []) =>
  [...items].sort((left, right) => {
    const cpfComparison = compareCpfValues(left?.colaborador_cpf, right?.colaborador_cpf);
    if (cpfComparison !== 0) return cpfComparison;

    const colaboradorComparison = String(left?.empresa || '').localeCompare(
      String(right?.empresa || ''),
      'pt-BR',
      { sensitivity: 'base' }
    );
    if (colaboradorComparison !== 0) return colaboradorComparison;

    const typeComparison =
      (DOCUMENTO_FUNCIONARIO_ORDER.get(left?.tipo_doc) ?? 999) -
      (DOCUMENTO_FUNCIONARIO_ORDER.get(right?.tipo_doc) ?? 999);
    if (typeComparison !== 0) return typeComparison;

    return String(left?.data_vencimento || '').localeCompare(String(right?.data_vencimento || ''));
  });

const sortTreinamentosFuncionarios = (items = []) =>
  [...items].sort((left, right) => {
    const cpfComparison = compareCpfValues(left?.colaborador_cpf, right?.colaborador_cpf);
    if (cpfComparison !== 0) return cpfComparison;

    const colaboradorComparison = String(left?.colaborador_nome || '').localeCompare(
      String(right?.colaborador_nome || ''),
      'pt-BR',
      { sensitivity: 'base' }
    );
    if (colaboradorComparison !== 0) return colaboradorComparison;

    const treinamentoComparison =
      (TREINAMENTO_FUNCIONARIO_ORDER.get(left?.treinamento_nome) ?? 999) -
      (TREINAMENTO_FUNCIONARIO_ORDER.get(right?.treinamento_nome) ?? 999);
    if (treinamentoComparison !== 0) return treinamentoComparison;

    return String(left?.data_vencimento || '').localeCompare(String(right?.data_vencimento || ''));
  });

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export function OcorrenciasTab({ token, tables }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/estrutura/historico`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        // Filter by tables relevant to this context
        const filtered = data.filter((log) => tables.includes(log.tabela));
        setLogs(filtered);
      }
    } catch (error) {
      console.error('Error fetching logs', error);
    } finally {
      setLoading(false);
    }
  }, [token, tables]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Histórico de Ocorrências
      </Typography>
      <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Data/Hora</TableCell>
              <TableCell>Usuário</TableCell>
              <TableCell>Ação</TableCell>
              <TableCell>Tabela</TableCell>
              <TableCell>ID Registro</TableCell>
              <TableCell>Detalhes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : logs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Nenhuma ocorrência encontrada.
                </TableCell>
              </TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>{formatDateTimeBr(log.data_hora)}</TableCell>
                  <TableCell>{log.usuario_nome}</TableCell>
                  <TableCell>{log.acao}</TableCell>
                  <TableCell>{log.tabela}</TableCell>
                  <TableCell>{log.registro_id}</TableCell>
                  <TableCell>
                    <Tooltip
                      title={
                        <div>
                          {log.valores_anteriores && (
                            <div>
                              <strong>Anterior:</strong> {log.valores_anteriores}
                            </div>
                          )}
                          {log.valores_novos && (
                            <div>
                              <strong>Novo:</strong> {log.valores_novos}
                            </div>
                          )}
                        </div>
                      }
                    >
                      <Button size="small">Ver Alterações</Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export function ModuleManager({
  title,
  endpoint,
  columns,
  fields,
  clienteId,
  token,
  requiresClient = true,
  dateField = null,
  tableName: _tableName,
  sortItems,
  validateForm,
  deriveFormData,
}) {
  const [openForm, setOpenForm] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState({});
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);

  const fetchItems = useCallback(async () => {
    if (requiresClient && !clienteId) {
      setItems([]);
      return;
    }

    setLoading(true);
    try {
      let url = `${API_URL}/estrutura${endpoint}`;
      if (requiresClient) {
        url += `/${clienteId}`;
      }

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setItems(data);
      } else {
        console.error('Failed to fetch items');
      }
    } catch (error) {
      console.error('Error fetching items', error);
    } finally {
      setLoading(false);
    }
  }, [endpoint, clienteId, token, requiresClient]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      return typeof deriveFormData === 'function'
        ? deriveFormData({ previous: prev, next, fieldName: field })
        : next;
    });
  };

  const handleFileChange = (field, file) => {
    setFiles((prev) => ({ ...prev, [field]: file }));
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData(item);
    setFiles({});
    setOpenForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este registro?')) return;

    try {
      const res = await fetch(`${API_URL}/estrutura${endpoint}/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        alert('Registro excluído com sucesso!');
        fetchItems();
      } else {
        alert('Erro ao excluir registro.');
      }
    } catch (error) {
      console.error('Error deleting item', error);
      alert('Erro ao excluir registro.');
    }
  };

  const handleSubmit = async () => {
    if (requiresClient && !clienteId) {
      alert('Selecione um cliente primeiro.');
      return;
    }

    const validationMessage =
      typeof validateForm === 'function' ? validateForm(formData, files) : '';
    if (validationMessage) {
      alert(validationMessage);
      return;
    }

    const data = new FormData();
    if (requiresClient && !editingId) {
      data.append('cliente_id', clienteId);
    }

    fields.forEach((field) => {
      if (field.type === 'file') return;

      const value = formData[field.name];
      if (value === null || value === undefined) return;
      if (field.type === 'date' && value === '') return;

      if (value !== '') {
        data.append(field.name, field.type === 'cpf' ? digitsOnly(value) : value);
      }
    });

    Object.keys(files).forEach((key) => {
      if (files[key]) {
        data.append(key, files[key]);
      }
    });

    try {
      const url = editingId
        ? `${API_URL}/estrutura${endpoint}/${editingId}`
        : `${API_URL}/estrutura${endpoint}/`;

      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method: method,
        headers: { Authorization: `Bearer ${token}` },
        body: data,
      });

      if (res.ok) {
        setFormData({});
        setFiles({});
        setOpenForm(false);
        setEditingId(null);
        fetchItems();
        alert(`Registro ${editingId ? 'atualizado' : 'criado'} com sucesso!`);
      } else {
        const payload = await res.json().catch(() => null);
        alert(
          payload?.detail ||
            payload?.error ||
            `Erro ao ${editingId ? 'atualizar' : 'criar'} registro.`
        );
      }
    } catch (error) {
      console.error('Error saving item', error);
      alert(`Erro ao ${editingId ? 'atualizar' : 'criar'} registro.`);
    }
  };

  const filteredItems = items.filter((item) => {
    if (!dateField || (!startDate && !endDate)) return true;
    const itemDateStr = item[dateField];
    if (!itemDateStr) return false;

    const itemDate = new Date(itemDateStr);
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    if (start) start.setHours(0, 0, 0, 0);
    if (end) end.setHours(23, 59, 59, 999);

    if (start && itemDate < start) return false;
    if (end && itemDate > end) return false;
    return true;
  });

  const visibleItems = sortItems ? sortItems(filteredItems) : filteredItems;

  const getFileUrl = (path) => {
    if (!path) return '';
    if (/^https?:\/\//i.test(path)) return path;
    const normalized = path.toString().replace(/\\/g, '/').replace(/^\/+/, '');
    return `/${normalized}`;
  };

  const getMediaType = (path) => {
    const ext = (path || '').toString().split('.').pop()?.toLowerCase() || '';
    const imageExt = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'];
    const videoExt = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'];
    if (imageExt.includes(ext)) return 'image';
    if (videoExt.includes(ext)) return 'video';
    return 'other';
  };

  const renderCellValue = (row, col) => {
    if (typeof col.renderCell === 'function') {
      return col.renderCell(row);
    }

    const value = row[col.field];
    if (value === null || value === undefined || value === '') return '-';

    if (col.type === 'date') {
      return formatDateBr(value);
    }

    if (col.type === 'cpf') {
      return formatCpf(value);
    }

    if (col.type === 'datetime') {
      return formatDateTimeBr(value);
    }

    if (col.field === 'criado_em') {
      return formatDateTimeBr(value);
    }

    if (col.field === 'arquivo') {
      const mediaType = getMediaType(value);
      const fileName = value.toString().split('/').pop();

      if (mediaType === 'image' || mediaType === 'video') {
        return (
          <Button
            size="small"
            onClick={() => {
              setPreviewItem({
                type: mediaType,
                url: getFileUrl(value),
                fileName,
                rowId: row.id,
                moduleTitle: title,
              });
              setPreviewOpen(true);
            }}
          >
            Visualizar {mediaType === 'image' ? 'Foto' : 'Vídeo'}
          </Button>
        );
      }

      return (
        <Button
          size="small"
          component="a"
          href={getFileUrl(value)}
          target="_blank"
          rel="noreferrer"
        >
          {fileName}
        </Button>
      );
    }

    return value;
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 2,
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {dateField && (
            <>
              <TextField
                label="De"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                size="small"
              />
              <TextField
                label="Até"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                size="small"
              />
            </>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => {
            setEditingId(null);
            setFormData({});
            setOpenForm(true);
          }}
        >
          Novo Registro
        </Button>
      </Box>

      {requiresClient && !clienteId ? (
        <Alert severity="warning">Selecione um cliente para visualizar os dados.</Alert>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={col.field} style={{ width: col.width }}>
                    {col.headerName}
                  </TableCell>
                ))}
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : visibleItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center">
                    Nenhum registro encontrado.
                  </TableCell>
                </TableRow>
              ) : (
                visibleItems.map((row) => (
                  <TableRow key={row.id}>
                    {columns.map((col) => (
                      <TableCell key={col.field}>{renderCellValue(row, col)}</TableCell>
                    ))}
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleEdit(row)} color="primary">
                        <Edit />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(row.id)} color="error">
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openForm} onClose={() => setOpenForm(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingId ? 'Editar' : 'Novo'} {title}{' '}
          {requiresClient && clienteId ? `- Cliente ID: ${clienteId}` : ''}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {fields.map((field) => (
                <Grid item xs={12} sm={field.width || 12} key={field.name}>
                  {field.type === 'file' ? (
                    <Box>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<CloudUpload />}
                        fullWidth
                      >
                        {field.label}
                        <input
                          type="file"
                          hidden
                          accept={field.accept}
                          onChange={(e) => handleFileChange(field.name, e.target.files[0])}
                        />
                        {files[field.name] && (
                          <Typography variant="caption" sx={{ ml: 1 }}>
                            {files[field.name].name}
                          </Typography>
                        )}
                      </Button>
                      {field.helperText ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.6, display: 'block' }}
                        >
                          {field.helperText}
                        </Typography>
                      ) : null}
                    </Box>
                  ) : field.type === 'select' ? (
                    <FormControl fullWidth>
                      <InputLabel>{field.label}</InputLabel>
                      <Select
                        label={field.label}
                        value={formData[field.name] || ''}
                        onChange={(e) => handleInputChange(field.name, e.target.value)}
                      >
                        <MenuItem value="">
                          <em>{field.emptyLabel || 'Selecione'}</em>
                        </MenuItem>
                        {(field.options || []).map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {field.helperText ? (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.6 }}>
                          {field.helperText}
                        </Typography>
                      ) : null}
                    </FormControl>
                  ) : field.type === 'date' ? (
                    <Box>
                      <TextField
                        label={field.label}
                        type="date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={formData[field.name] || ''}
                        onChange={(e) => handleInputChange(field.name, e.target.value)}
                      />
                      {field.helperText ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.6, display: 'block' }}
                        >
                          {field.helperText}
                        </Typography>
                      ) : null}
                    </Box>
                  ) : (
                    <TextField
                      label={field.label}
                      fullWidth
                      multiline={Boolean(field.multiline)}
                      rows={field.rows}
                      value={
                        field.type === 'cpf'
                          ? formatCpf(formData[field.name] || '')
                          : formData[field.name] || ''
                      }
                      onChange={(e) =>
                        handleInputChange(
                          field.name,
                          field.type === 'cpf' ? formatCpf(e.target.value) : e.target.value
                        )
                      }
                      helperText={
                        field.type === 'cpf' && formData[field.name]
                          ? isValidCpf(formData[field.name])
                            ? field.helperText
                            : 'Informe um CPF valido.'
                          : field.helperText
                      }
                      error={
                        field.type === 'cpf' &&
                        Boolean(formData[field.name]) &&
                        !isValidCpf(formData[field.name])
                      }
                      inputProps={field.type === 'cpf' ? { inputMode: 'numeric' } : undefined}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenForm(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmit}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Visualização de Mídia {previewItem?.rowId ? `- Registro ${previewItem.rowId}` : ''}
        </DialogTitle>
        <DialogContent>
          {!previewItem ? null : previewItem.type === 'image' ? (
            <Box sx={{ textAlign: 'center' }}>
              <img
                src={previewItem.url}
                alt={previewItem.fileName || 'Foto'}
                style={{ maxWidth: '100%', maxHeight: 520, borderRadius: 8 }}
              />
            </Box>
          ) : previewItem.type === 'video' ? (
            <Box sx={{ textAlign: 'center' }}>
              <video controls style={{ width: '100%', maxHeight: 520, borderRadius: 8 }}>
                <source src={previewItem.url} />
                Seu navegador não suporta reprodução de vídeo.
              </video>
            </Box>
          ) : (
            <Alert severity="info">Arquivo sem visualização de mídia.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export function EstruturaObra({ clienteId, token }) {
  const [tabIndex, setTabIndex] = useState(0);
  const modules = [
    {
      title: 'Cronograma',
      icon: <CalendarMonth />,
      endpoint: '/cronogramas',
      tableName: 'cronogramas',
      dateField: 'data_inicio',
      columns: [
        { field: 'id', headerName: 'ID', width: 70 },
        { field: 'especialidade', headerName: 'Especialidade', width: 200 },
        { field: 'data_inicio', headerName: 'Início', width: 130 },
        { field: 'data_termino', headerName: 'Término', width: 130 },
      ],
      fields: [
        { name: 'especialidade', label: 'Especialidade', width: 12 },
        { name: 'data_inicio', label: 'Data Início', type: 'date', width: 6 },
        { name: 'data_termino', label: 'Data Término', type: 'date', width: 6 },
      ],
    },
    {
      title: 'Diário de Obra',
      icon: <Description />,
      endpoint: '/diarios',
      tableName: 'diarios',
      dateField: 'data',
      columns: [
        { field: 'id', headerName: 'ID', width: 70 },
        { field: 'data', headerName: 'Data', width: 130 },
        { field: 'descricao', headerName: 'Descrição', width: 300 },
        { field: 'assinatura_gestor', headerName: 'Assinatura', width: 200 },
      ],
      fields: [
        { name: 'data', label: 'Data', type: 'date', width: 12 },
        { name: 'descricao', label: 'Descrição', width: 12 },
        { name: 'assinatura', label: 'Assinatura (Img)', type: 'file', width: 12 },
      ],
    },
    {
      title: 'Fotos',
      icon: <PhotoCamera />,
      endpoint: '/fotos',
      tableName: 'fotos',
      dateField: 'criado_em',
      columns: [
        { field: 'id', headerName: 'ID', width: 70 },
        { field: 'local', headerName: 'Local', width: 200 },
        { field: 'arquivo', headerName: 'Arquivo', width: 300 },
      ],
      fields: [
        { name: 'local', label: 'Local', width: 12 },
        { name: 'arquivo', label: 'Foto', type: 'file', width: 12 },
      ],
    },
    {
      title: 'Materiais',
      icon: <Inventory />,
      endpoint: '/pedidos',
      tableName: 'pedidos',
      dateField: 'prazo',
      columns: [
        { field: 'id', headerName: 'ID', width: 70 },
        { field: 'descricao', headerName: 'Descrição', width: 250 },
        { field: 'prazo', headerName: 'Prazo', width: 130 },
        { field: 'previsao_entrega', headerName: 'Previsão', width: 130 },
      ],
      fields: [
        { name: 'descricao', label: 'Descrição', width: 12 },
        { name: 'prazo', label: 'Prazo', type: 'date', width: 6 },
        { name: 'previsao_entrega', label: 'Previsão Entrega', type: 'date', width: 6 },
      ],
    },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabIndex}
          onChange={(e, v) => setTabIndex(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {modules.map((m, i) => (
            <Tab key={i} label={m.title} icon={m.icon} iconPosition="start" />
          ))}
          <Tab label="Ocorrências" icon={<History />} iconPosition="start" />
        </Tabs>
      </Box>
      {modules.map((m, i) => (
        <TabPanel value={tabIndex} index={i} key={i}>
          <ModuleManager
            title={m.title}
            endpoint={m.endpoint}
            tableName={m.tableName}
            columns={m.columns}
            fields={m.fields}
            clienteId={clienteId}
            token={token}
            requiresClient={true}
            dateField={m.dateField}
            sortItems={m.sortItems}
            validateForm={m.validateForm}
            deriveFormData={m.deriveFormData}
          />
        </TabPanel>
      ))}
      <TabPanel value={tabIndex} index={modules.length}>
        <OcorrenciasTab token={token} tables={modules.map((m) => m.tableName)} />
      </TabPanel>
    </Box>
  );
}

export function EstruturaSeguranca({ clienteId, token }) {
  const [tabIndex, setTabIndex] = useState(0);
  const modules = [
    {
      title: 'EPIs',
      icon: <Engineering />,
      endpoint: '/epis',
      tableName: 'epis',
      requiresClient: true,
      dateField: 'criado_em',
      columns: [
        { field: 'id', headerName: 'ID', width: 70 },
        { field: 'tipo', headerName: 'Tipo', width: 150 },
        { field: 'quantidade', headerName: 'Qtd', width: 100 },
        { field: 'funcionario_responsavel', headerName: 'Responsável', width: 200 },
        { field: 'criado_em', headerName: 'Data/Hora Registro', width: 190 },
      ],
      fields: [
        { name: 'tipo', label: 'Tipo EPI', width: 6 },
        { name: 'quantidade', label: 'Quantidade', width: 6 },
        { name: 'funcionario_responsavel', label: 'Funcionário Responsável', width: 12 },
      ],
    },
    {
      title: 'Documentos Funcionários',
      icon: <Assignment />,
      endpoint: '/docs-funcionarios',
      tableName: 'docs-funcionarios',
      requiresClient: false,
      dateField: 'data_vencimento',
      sortItems: sortDocumentosFuncionarios,
      validateForm: validateDocumentoFuncionarioForm,
      columns: [
        { field: 'id', headerName: 'ID', width: 70 },
        { field: 'empresa', headerName: 'Colaborador', width: 220 },
        { field: 'colaborador_cpf', headerName: 'CPF', width: 150, type: 'cpf' },
        { field: 'tipo_doc', headerName: 'Documento', width: 220 },
        { field: 'data_vencimento', headerName: 'Vencimento', width: 140, type: 'date' },
        {
          field: 'status_vencimento',
          headerName: 'Status',
          width: 220,
          renderCell: (row) => renderExpirationStatusCell(row.data_vencimento),
        },
        { field: 'arquivo', headerName: 'Arquivo', width: 200 },
      ],
      fields: [
        {
          name: 'empresa',
          label: 'Colaborador',
          width: 6,
          helperText: 'Informe o nome do colaborador exibido no registro do documento.',
        },
        {
          name: 'colaborador_cpf',
          label: 'CPF do colaborador',
          type: 'cpf',
          width: 6,
          helperText: 'Obrigatorio. O registro fica vinculado ao CPF informado.',
        },
        {
          name: 'tipo_doc',
          label: 'Tipo Documento',
          type: 'select',
          width: 6,
          options: DOCUMENTO_FUNCIONARIO_OPTIONS,
        },
        {
          name: 'data_vencimento',
          label: 'Vencimento',
          type: 'date',
          width: 6,
          helperText: 'Obrigatório para ASO. Use também quando houver validade de outro documento.',
        },
        {
          name: 'arquivo',
          label: 'Arquivo',
          type: 'file',
          width: 6,
          accept: '.pdf,.jpg,.jpeg,.png',
        },
      ],
    },
    {
      title: 'Treinamentos',
      icon: <VerifiedUser />,
      endpoint: '/treinamentos-funcionarios',
      tableName: 'treinamentos-funcionarios',
      requiresClient: false,
      dateField: 'data_vencimento',
      sortItems: sortTreinamentosFuncionarios,
      validateForm: validateTreinamentoFuncionarioForm,
      deriveFormData: deriveTrainingFormData,
      columns: [
        { field: 'id', headerName: 'ID', width: 70 },
        { field: 'colaborador_nome', headerName: 'Colaborador', width: 220 },
        { field: 'colaborador_cpf', headerName: 'CPF', width: 150, type: 'cpf' },
        { field: 'treinamento_nome', headerName: 'Treinamento', width: 160 },
        { field: 'data_realizacao', headerName: 'Realização', width: 140, type: 'date' },
        { field: 'data_vencimento', headerName: 'Vencimento', width: 140, type: 'date' },
        {
          field: 'status_vencimento',
          headerName: 'Status',
          width: 220,
          renderCell: (row) => renderExpirationStatusCell(row.data_vencimento),
        },
        { field: 'arquivo', headerName: 'Comprovante', width: 220 },
      ],
      fields: [
        { name: 'colaborador_nome', label: 'Colaborador', width: 6 },
        {
          name: 'colaborador_cpf',
          label: 'CPF do colaborador',
          type: 'cpf',
          width: 6,
          helperText: 'Obrigatorio. O treinamento fica vinculado ao CPF informado.',
        },
        {
          name: 'treinamento_nome',
          label: 'Treinamento',
          type: 'select',
          width: 6,
          options: TREINAMENTO_FUNCIONARIO_OPTIONS,
        },
        { name: 'data_realizacao', label: 'Data de realização', type: 'date', width: 6 },
        {
          name: 'data_vencimento',
          label: 'Data de vencimento',
          type: 'date',
          width: 6,
          helperText: 'NR 10 e NR 35 preenchem automaticamente 2 anos após a realização.',
        },
        {
          name: 'arquivo',
          label: 'Certificado / comprovante',
          type: 'file',
          width: 6,
          accept: '.pdf,.jpg,.jpeg,.png',
        },
        {
          name: 'observacoes',
          label: 'Observações',
          width: 6,
          multiline: true,
          rows: 3,
          helperText:
            'Use para registrar quando o treinamento é aplicável conforme atividade ou risco.',
        },
      ],
    },
    {
      title: 'Programas',
      icon: <HealthAndSafety />,
      endpoint: '/programas-seguranca',
      tableName: 'programas-seguranca',
      requiresClient: false,
      dateField: 'criado_em',
      columns: [
        { field: 'id', headerName: 'ID', width: 70 },
        { field: 'tipo_doc', headerName: 'Tipo Doc', width: 200 },
        { field: 'arquivo', headerName: 'Arquivo', width: 300 },
        { field: 'criado_em', headerName: 'Data/Hora Registro', width: 190 },
      ],
      fields: [
        { name: 'tipo_doc', label: 'Tipo Documento (PGR, PCMSO...)', width: 12 },
        { name: 'arquivo', label: 'Arquivo', type: 'file', width: 12 },
      ],
    },
    {
      title: 'Relatório TST',
      icon: <VerifiedUser />,
      endpoint: '/relatorios-tst',
      tableName: 'relatorios-tst',
      requiresClient: false,
      dateField: 'data',
      columns: [
        { field: 'id', headerName: 'ID', width: 70 },
        { field: 'data', headerName: 'Data', width: 130 },
        { field: 'observacoes', headerName: 'Observações', width: 300 },
        { field: 'criado_em', headerName: 'Data/Hora Registro', width: 190 },
      ],
      fields: [
        { name: 'data', label: 'Data', type: 'date', width: 12 },
        { name: 'observacoes', label: 'Observações', width: 12 },
        { name: 'arquivo', label: 'Relatório (PDF)', type: 'file', width: 12 },
      ],
    },
  ];

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabIndex}
          onChange={(e, v) => setTabIndex(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {modules.map((m, i) => (
            <Tab key={i} label={m.title} icon={m.icon} iconPosition="start" />
          ))}
          <Tab label="Ocorrências" icon={<History />} iconPosition="start" />
        </Tabs>
      </Box>
      {modules.map((m, i) => (
        <TabPanel value={tabIndex} index={i} key={i}>
          <ModuleManager
            title={m.title}
            endpoint={m.endpoint}
            tableName={m.tableName}
            columns={m.columns}
            fields={m.fields}
            clienteId={clienteId}
            token={token}
            requiresClient={m.requiresClient}
            dateField={m.dateField}
            sortItems={m.sortItems}
            validateForm={m.validateForm}
            deriveFormData={m.deriveFormData}
          />
        </TabPanel>
      ))}
      <TabPanel value={tabIndex} index={modules.length}>
        <OcorrenciasTab token={token} tables={modules.map((m) => m.tableName)} />
      </TabPanel>
    </Box>
  );
}

export default function Estrutura() {
  const location = useLocation();
  const [activeModule, setActiveModule] = useState(null);
  const [selectedClienteId, setSelectedClienteId] = useState('');
  const [clientes, setClientes] = useState([]);
  const { token, isManutencao } = useAuth();
  const manutencaoUser = isManutencao();

  useEffect(() => {
    if (location.state?.module) {
      setActiveModule(location.state.module);
    }
  }, [location.state]);

  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };

        // Verificar quais clientes o usuário tem acesso
        let clienteIds = null;
        try {
          const meResponse = await fetch(`${API_URL}/me/clientes`, { headers });
          if (meResponse.ok) {
            const meData = await meResponse.json();
            clienteIds = meData.clientes || [];
          }
        } catch (e) {
          console.warn('Endpoint /me/clientes não disponível');
        }

        const res = await fetch(`${API_URL}/clientes/`, { headers });
        if (res.ok) {
          let data = await res.json();
          // Filtrar se houver restrição
          if (clienteIds && clienteIds.length > 0) {
            data = data.filter((c) => clienteIds.map(Number).includes(Number(c.id)));
          }
          setClientes(data);

          // Manutenção: pré-selecionar automaticamente a primeira loja permitida
          if (manutencaoUser && data.length > 0) {
            setSelectedClienteId((prev) => {
              const alreadyValid = data.some((c) => String(c.id) === String(prev));
              return alreadyValid ? prev : data[0].id;
            });
          }
        }
      } catch (error) {
        console.error('Erro ao buscar clientes', error);
      }
    };
    if (token) fetchClientes();
  }, [token, manutencaoUser]);

  const handleModuleClick = (module) => {
    setActiveModule(module);
  };

  const handleBack = () => {
    setActiveModule(null);
  };

  if (!activeModule) {
    return (
      <Layout title="Estrutura">
        <Box sx={{ p: 2 }}>
          <Typography variant="h5" sx={{ mb: 4, fontWeight: 'bold' }}>
            Selecione o Módulo
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'scale(1.02)' },
                }}
                onClick={() => handleModuleClick('obra')}
              >
                <CardActionArea sx={{ height: '100%', p: 4 }}>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
                  >
                    <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main' }}>
                      <Construction sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Typography variant="h4" component="div">
                      Estrutura de Obra
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Cronograma, Diário, Fotos e Materiais
                    </Typography>
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'scale(1.02)' },
                }}
                onClick={() => handleModuleClick('seguranca')}
              >
                <CardActionArea sx={{ height: '100%', p: 4 }}>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}
                  >
                    <Avatar sx={{ width: 80, height: 80, bgcolor: 'secondary.main' }}>
                      <Security sx={{ fontSize: 40 }} />
                    </Avatar>
                    <Typography variant="h4" component="div">
                      Estrutura Segurança
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      EPIs, Documentos, Programas e Relatórios
                    </Typography>
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title={activeModule === 'obra' ? 'Estrutura de Obra' : 'Estrutura Segurança'}>
      <Box sx={{ mb: 3, px: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBack />} onClick={handleBack} variant="outlined">
          Voltar
        </Button>

        <FormControl sx={{ minWidth: 250 }} size="small">
          <InputLabel>Selecione o Cliente (Obra)</InputLabel>
          <Select
            value={selectedClienteId}
            label="Selecione o Cliente (Obra)"
            onChange={(e) => setSelectedClienteId(e.target.value)}
          >
            <MenuItem value="">
              <em>Nenhum</em>
            </MenuItem>
            {clientes.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.nome}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ px: 2 }}>
        {activeModule === 'obra' ? (
          <EstruturaObra clienteId={selectedClienteId} token={token} />
        ) : (
          <EstruturaSeguranca clienteId={selectedClienteId} token={token} />
        )}
      </Box>
    </Layout>
  );
}
